import { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet, apiPatch } from "../apiClient";
import {
  DELIVERY_STATUSES,
  PROFILE_TYPE_AI_AGENT,
  PROFILE_TYPE_HUMAN,
  VIEW_SCOPE_WORKSPACE
} from "../domainCatalog";
import "./BoardPage.css";


const BoardPage = ({ token, scope = "workstream", onOpenTicket }) => {
  const [tickets, setTickets] = useState([]);
  const [team, setTeam] = useState([]);
  const [developerFilter, setDeveloperFilter] = useState("all");
  const [draggedId, setDraggedId] = useState(null);
  const [message, setMessage] = useState("");
  const workspaceView = scope === VIEW_SCOPE_WORKSPACE;

  const load = useCallback(async () => {
    try {
      const query = workspaceView ? "?scope=workspace" : "";
      const [backlog, teamResponse] = await Promise.all([
        apiGet(`/task-studio/tickets${query}`, { token }),
        apiGet("/team", { token })
      ]);
      setTickets(backlog.tickets || []);
      setTeam(teamResponse.members || []);
      setMessage("");
    } catch (error) {
      setMessage(error.message || "Could not load the board.");
    }
  }, [token, workspaceView]);

  useEffect(() => { load(); }, [load]);

  const membersById = useMemo(
    () => Object.fromEntries(team.map((member) => [member.id, member])),
    [team]
  );
  const humanMembers = useMemo(
    () => team.filter((member) => (member.profile_type || PROFILE_TYPE_HUMAN) === PROFILE_TYPE_HUMAN),
    [team]
  );
  const filteredTickets = useMemo(
    () => tickets.filter((ticket) => developerFilter === "all"
      || (developerFilter === "unassigned" ? !ticket.assignee_id : ticket.assignee_id === Number(developerFilter))),
    [developerFilter, tickets]
  );

  const patchTicket = async (ticketId, changes) => {
    const previous = tickets;
    setTickets((current) => current.map((ticket) => ticket.id === ticketId ? { ...ticket, ...changes } : ticket));
    setMessage("");
    try {
      const updated = await apiPatch(`/task-studio/tickets/${ticketId}/board`, changes, { token });
      setTickets((current) => current.map((ticket) => ticket.id === ticketId ? updated : ticket));
    } catch (error) {
      setTickets(previous);
      setMessage(error.message || "Could not update the card.");
    }
  };

  const moveTicket = (status) => {
    if (!draggedId || workspaceView) return;
    const ticket = tickets.find((item) => item.id === draggedId);
    setDraggedId(null);
    if (!ticket || ticket.delivery_status === status) return;
    patchTicket(ticket.id, { delivery_status: status });
  };

  const assignTicket = (ticket, value) => {
    if (workspaceView) return;
    const assigneeId = Number(value) || null;
    const assignee = membersById[assigneeId];
    const changes = { assignee_id: assigneeId };
    if (!assignee || assignee.profile_type !== PROFILE_TYPE_AI_AGENT) {
      changes.reviewer_id = null;
    } else {
      changes.reviewer_id = ticket.reviewer_id || humanMembers[0]?.id || null;
    }
    patchTicket(ticket.id, changes);
  };

  return (
    <div className="board-page">
      <header className="board-header">
        <div><p>Delivery</p><h1>Board</h1></div>
        <label>Developer
          <select value={developerFilter} onChange={(event) => setDeveloperFilter(event.target.value)}>
            <option value="all">All developers</option>
            <option value="unassigned">Unassigned</option>
            {team.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
          </select>
        </label>
      </header>
      {workspaceView && <div className="board-notice">Workspace board is read-only. Select a workstream to move or assign cards.</div>}
      {message && <div className="board-error" role="alert">{message}</div>}
      <div className="board-columns">
        {DELIVERY_STATUSES.map((status) => {
          const columnTickets = filteredTickets.filter((ticket) => ticket.delivery_status === status);
          return (
            <section
              className="board-column"
              key={status}
              onDragOver={(event) => !workspaceView && event.preventDefault()}
              onDrop={() => moveTicket(status)}
            >
              <header><h2>{status}</h2><span>{columnTickets.length}</span></header>
              <div className="board-card-list">
                {columnTickets.map((ticket) => (
                  <article
                    className="board-card"
                    draggable={!workspaceView}
                    key={ticket.id}
                    onDragStart={() => setDraggedId(ticket.id)}
                    onDragEnd={() => setDraggedId(null)}
                  >
                    <button className="board-card-title" onClick={() => onOpenTicket?.(ticket.id)}>
                      <small>{ticket.jira_issue_key || ticket.ticket_type}{ticket.workstream_key ? ` · ${ticket.workstream_key}` : ""}</small>
                      <strong>{ticket.title}</strong>
                    </button>
                    <div className="board-card-meta"><span>{ticket.complexity} pts</span>{ticket.ticket_type === "Sub-task" && <span>Sub-task</span>}</div>
                    <label>Developer
                      <select value={ticket.assignee_id || ""} disabled={workspaceView} onChange={(event) => assignTicket(ticket, event.target.value)}>
                        <option value="">Unassigned</option>
                        {team.map((member) => <option key={member.id} value={member.id}>{member.name}{member.profile_type === PROFILE_TYPE_AI_AGENT ? " · AI" : ""}</option>)}
                      </select>
                    </label>
                    {ticket.assignee_id && membersById[ticket.assignee_id]?.profile_type === PROFILE_TYPE_AI_AGENT && (
                      <label>Reviewer
                        <select
                          value={ticket.reviewer_id || ""}
                          disabled={workspaceView}
                          onChange={(event) => patchTicket(ticket.id, { reviewer_id: Number(event.target.value) || null })}
                        >
                          <option value="">Select reviewer</option>
                          {humanMembers.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
                        </select>
                      </label>
                    )}
                  </article>
                ))}
                {!columnTickets.length && <div className="board-empty">Drop tickets here</div>}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default BoardPage;
